import { Building } from "~/components/blocks/common/Interfaces/BuildingsInterface";
import { Listing, ListingArea, PropertyListing } from "~/components/blocks/common/Interfaces/ListingsInterface";
import { Property } from "~/components/blocks/common/Interfaces/PropertiesInterface";
import { DEFAULT_REGION } from "~/lib/constants/generalConstants";
import { areaSlug, buildingSlug, listingSlug, propertySlug } from "~/lib/helpers/slugHelper";
import { useParams } from "@remix-run/react";
import { Workspace } from "~/components/blocks/Geosearch/interfaces";
import { getRegionConfigByCountry } from "custom-config/region.configs";
import { useUserLocationStore } from "~/store/userLocation";
import { getSearchPathSlugs } from "~/lib/helpers/ipLocationSearchPathHelper";
import {
    buildCountryPropertyPagePath,
    buildPropertyNavCityUrl,
} from "~/components/blocks/Geosearch/locationGeosearch";
import { useMemo } from "react";

export const DomainNameWithSubPathForLocale = (
    region?: string,
    requestUrl?: string,
) => {
    // Get the base domain from environment or request
    let baseDomain = "https://www.office-hub.com"; // Default fallback

    if (requestUrl) {
        // Use the request URL to get the current domain
        const url = new URL(requestUrl);
        baseDomain = url.origin;
    } else if (process.env.NODE_ENV === "development") {
        // In development, use localhost
        baseDomain = "http://localhost:5173";
    } else if (process.env.REMIX_APP_DOMAIN) {
        // Use environment variable if set
        baseDomain = process.env.REMIX_APP_DOMAIN;
    }

    if (!region || region.length !== 2 || region === DEFAULT_REGION) {
        return baseDomain;
    }

    return baseDomain + "/" + region;
};
export const GetDomainName = () => {
    // Check if we're in a browser environment
    if (typeof window !== 'undefined') {
        return window.location.origin;
    }

    // Fallback for server-side
    if (process.env.NODE_ENV === "development") {
        return "http://localhost:5173";
    } else if (process.env.REMIX_APP_DOMAIN) {
        return process.env.REMIX_APP_DOMAIN;
    }

    return "https://www.office-hub.com"; // Default fallback
};
export const currentURL = (
    region?: string,
    data?: Listing | Property | Building | ListingArea | Workspace,
    type?: string,
): string => {
    const baseUrl = GetDomainName();

    if (!type) return baseUrl;

    let slug = "";
    switch (type) {
        case "listing":
            slug = listingSlug(data as Listing);
            break;
        case "property":
            slug = propertySlug(data as PropertyListing);
            break;
        case "building":
            slug = buildingSlug(data as Building);
            break;
        case "area":
            slug = areaSlug(data as Workspace);
            break;
        default:
            throw new Error("Unknown payload type");
    }

    const UTM =
        "?utm_source=OfficeHub&utm_medium=web&utm_campaign=share-" +
        type;

    return baseUrl + slug + UTM;
};

export function maybeShowHrefLangAlts(path: string = "/"): boolean {
    const excluded = [
        "/404",
        "/iframe",
        "/choose-country-region",
        // breadcrumbs handled per page type
        "/listings/",
        "/buildings/",
        "/properties/",
        "/workspaces/",
        "/areas",
        "/blog"
    ];

    // Return true if none of the excluded substrings are present in path
    return !excluded.some(excludedPath => path.includes(excludedPath));
}
// below function is a general function to get route based on existing country url based
export function getLocalizedLink(link: string) {
    const params = useParams();
    const country = params.country;
    const cleanLink = link.replace(/^\//, "");
    return country ? `/${country}/${cleanLink}` : `/${cleanLink}`;
}


type RedirectEntity = {
    country?: string
    name?: string
    id?: string | number
    sfid?: string
    // Add any other fields you need for canonical slug generation
}

type GetCanonicalSlug = (entity: RedirectEntity) => string | undefined

interface CountrySlugRedirectArgs {
    entity: RedirectEntity
    country: string | undefined
    request: Request
    getCanonicalSlug?: GetCanonicalSlug
}

export function handleCountryAndSlugRedirect({
    entity,
    country,
    request,
    getCanonicalSlug,
}: CountrySlugRedirectArgs): string | null {
    const url = new URL(request.url)
    const pathParts = url.pathname.split("/").filter(Boolean)

    // If entity.country is not present, skip country-based redirects
    if (!entity?.country) {
        // Only check for canonical slug if getCanonicalSlug is provided
        if (getCanonicalSlug) {
            const canonicalSlug = getCanonicalSlug(entity)
            if (canonicalSlug) {
                const currentSlug = pathParts[pathParts.length - 1]
                if (currentSlug !== canonicalSlug) {
                    const basePath = pathParts.slice(0, -1).join("/")
                    return `/${basePath}/${canonicalSlug}${url.search}`
                }
            }
        }
        return null
    }

    const regionConfig = getRegionConfigByCountry(entity.country)

    // Handle country prefix redirect
    if (country && regionConfig?.region !== country) {
        // Special case for US: allow both with and without country prefix
        if (regionConfig?.region === "us" && country === "us") {
            return null
        }

        const basePathParts = pathParts.slice(1)
        const basePath = basePathParts.join("/")
        const expectedPath = regionConfig?.region === "us"
            ? `/${basePath}`
            : `/${regionConfig?.region}/${basePath}`

        return `${expectedPath}${url.search}`
    }

    // Handle missing country prefix for non-US regions
    if (!country &&
        entity.country.toLowerCase() !== "united states" &&
        regionConfig?.region &&
        regionConfig?.region !== "us") {
        const basePath = pathParts.join("/")
        return `/${regionConfig.region}/${basePath}${url.search}`
    }

    // Handle canonical slug redirect only if getCanonicalSlug is provided
    if (getCanonicalSlug) {
        const canonicalSlug = getCanonicalSlug(entity)
        if (canonicalSlug) {
            const currentSlug = pathParts[pathParts.length - 1]
            if (currentSlug !== canonicalSlug) {
                const basePath = pathParts.slice(0, -1).join("/")
                return `/${basePath}/${canonicalSlug}${url.search}`
            }
        }
    }

    return null
}

function addMinutes12h(dateStr: string, timeStr: string, add: number) {
    const [time, apRaw] = timeStr.trim().split(/\s+/);
    let [h, m] = time.split(':').map(n => parseInt(n, 10));
    const ap = apRaw.toUpperCase();
    if (ap === 'PM' && h !== 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;

    let total = h * 60 + (m || 0) + add;
    let dayOffset = Math.floor(total / 1440);
    total = ((total % 1440) + 1440) % 1440;

    let hh24 = Math.floor(total / 60);
    let mm = total % 60;
    const ap2 = hh24 >= 12 ? 'PM' : 'AM';
    let h12 = hh24 % 12; if (h12 === 0) h12 = 12;

    const hStr = String(h12).padStart(2, '0');
    const mStr = String(mm).padStart(2, '0');

    let [Y, M, D] = dateStr.split('-').map(Number);
    if (dayOffset) {
        const d = new Date(Date.UTC(Y, M - 1, D));
        d.setUTCDate(d.getUTCDate() + dayOffset);
        Y = d.getUTCFullYear(); M = d.getUTCMonth() + 1; D = d.getUTCDate();
    }
    const mm2 = String(M).padStart(2, '0');
    const dd = String(D).padStart(2, '0');

    return `${Y}-${mm2}-${dd} ${hStr}:${mStr} ${ap2}`;
}

function padTime12h(timeStr: string) {
    const [time, ap] = timeStr.trim().split(/\s+/);
    let [h, m] = time.split(':');
    return `${String(parseInt(h, 10)).padStart(2, '0')}:${String(parseInt(m || '0', 10)).padStart(2, '0')} ${ap.toUpperCase()}`;
}

export function buildAddEventLink(userData: any, data: any) {
    const listing = userData?.lead?.listings?.[0];
    const date = listing?.tour_date;   // e.g. "2025-10-21"
    const time = listing?.tour_time;   // e.g. "3:00 PM"
    if (!date || !time) return '';

    const start = `${date} ${padTime12h(time)}`;
    const end = addMinutes12h(date, time, 30);

    const params = new URLSearchParams({
        client: 'akDRrQJvhzQwoUJpImwQ48643',
        start,
        end,
        title: `Tour Booking: ${data?.addressFormula || data?.name} (Office Hub)`,
        location: `${data?.workspaceProviderName}, ${data?.addressFormula} `,
        organizer: 'Office Hub',
        organizer_email: 'enquiries@office-hub.com',
        duration: '30',
        status: 'CONFIRMED',
        description: `Provider: ${data?.workspaceProviderName}<br />Address: ${data?.addressFormula}`,
    });

    return `https://dynamic.addevent.com/dir/?${params.toString()}`;
}

export function CurrentLocationSearchUrl() {
    const { location } = useUserLocationStore();

    const searchUrl = useMemo(() => {
        const countryCode =
            location?.countryCode?.toLowerCase() === "gb"
                ? "uk"
                : location?.countryCode?.toLowerCase() || "us";
        const { stateSlug, citySlug } = getSearchPathSlugs(location);

        if (!stateSlug || !citySlug) {
            return buildCountryPropertyPagePath(countryCode);
        }

        return buildPropertyNavCityUrl(`/${countryCode}`, stateSlug, citySlug);
    }, [location]);
    return searchUrl;
}